import { getByID } from '@adornis/baseql/operations/mongo.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { LocalTicket } from '../../db/LocalTicket.js';
import { renderStructuredDescription } from './ticket-description.js';

export const LocalTicketDetail = stateful(({ ticketID }: { ticketID: string }) => {
  const ticketQuery = useQuery(
    () =>
      getByID(
        LocalTicket,
        ticketID,
      )({
        ticketNumber: 1,
        crmStatus: 1,
        crmTicketNumber: 1,
        manualReviewReason: 1,
        queueStatus: 1,
        desiredQueueName: 1,
        escalationStatus: 1,
        phoneNumber: 1,
        callerName: 1,
        callerEmail: 1,
        accountName: 1,
        accountNumber: 1,
        dossierNumber: 1,
        machineName: 1,
        description: 1,
        status: 1,
        priority: 1,
        resolution: 1,
        escalatedOn: 1,
        incompleteCapture: 1,
        notes: 1,
        knowledgeQueue: 1,
        createdOn: 1,
        modifiedOn: 1,
      }),
    [ticketID],
  );

  const loading = ticketQuery.loading;
  const ticket = ticketQuery.value;

  const [logOpen, setLogOpen] = useState(false);

  const handleBack = () => window.history.back();

  if (loading) {
    return html`
      <section class="container mx-auto py-8 px-4">
        <div class="card max-w-xl mx-auto">
          <div class="card-body flex flex-col items-center justify-center min-h-[200px]">
            <span class="loading loading-spinner loading-lg text-primary mb-4"></span>
            <span class="text-base-content/70">Lade Ticketdaten ...</span>
          </div>
        </div>
      </section>
    `;
  }

  if (!ticket) {
    return html`
      <section class="container mx-auto py-8 px-4">
        <div class="card max-w-xl mx-auto">
          <div class="card-body">
            <div class="alert alert-error mb-4">
              <span>Ticket ${ticketID} nicht gefunden</span>
            </div>
            <button class="btn btn-ghost" @click=${handleBack}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="inline w-5 h-5 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Zurück
            </button>
          </div>
        </div>
      </section>
    `;
  }

  return html`
    <section class="container mx-auto py-8 px-4">
      <div class="card max-w-xl mx-auto" tabindex="0">
        <div class="card-body">
          <button class="btn btn-ghost mb-4" @click=${handleBack}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="inline w-5 h-5 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Zurück
          </button>
          <h2 class="card-title text-2xl font-bold text-primary mb-6">Ticket Details</h2>

          ${ticket.crmStatus && ticket.crmStatus !== 'confirmed'
            ? html`<div class="alert alert-warning mb-4">
                CRM-Ticket nicht bestätigt. Bitte manuell prüfen; vor einer Neuanlage den VOICE-REQUEST-Vermerk im CRM
                suchen. ${ticket.manualReviewReason ?? ''}
              </div>`
            : nothing}
          <div>
            CRM: ${ticket.crmTicketNumber ?? 'Nicht bestätigt'} · Zuweisung: ${ticket.queueStatus ?? '-'} · Ziel:
            ${ticket.desiredQueueName ?? '-'}
          </div>
          ${ticket.queueStatus === 'manual-review'
            ? html`<div class="alert alert-warning">Zuweisung fehlgeschlagen — bitte manuell zuordnen.</div>`
            : nothing}
          ${ticket.incompleteCapture
            ? html`<div class="alert alert-warning mb-4">Ticket nicht vollständig verifiziert — bitte prüfen.</div>`
            : nothing}

          <div class="mb-6 space-y-2">
            <div class="flex justify-between">
              <span class="font-semibold">Ticket ID:</span><span>${ticket.ticketNumber ?? '-'}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Telefonnummer:</span><span>${ticket.phoneNumber ?? '-'}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Erstellt am:</span
              ><span>${ticket.createdOn ? format(ticket.createdOn, 'dd.MM.yyyy HH:mm') : '-'}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Name:</span><span>${ticket.callerName ?? '-'}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Knowledge Queue:</span><span>${ticket.knowledgeQueue ?? '-'}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Status:</span><span>${ticket.status ?? '-'}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Priorität:</span><span>${ticket.priority ?? '-'}</span>
            </div>
            ${ticket.escalatedOn
              ? html`<div class="flex justify-between">
                  <span class="font-semibold">Eskaliert am:</span
                  ><span>${format(ticket.escalatedOn, 'dd.MM.yyyy HH:mm')}</span>
                </div>`
              : nothing}
            <div class="flex flex-col gap-0.5">
              <span class="font-semibold">Problem:</span>${renderStructuredDescription(ticket.description)}
            </div>
            ${ticket.resolution
              ? html`<div class="flex flex-col gap-0.5">
                  <span class="font-semibold">Lösung:</span>${renderStructuredDescription(ticket.resolution)}
                </div>`
              : nothing}
          </div>

          ${ticket.machineName
            ? html`
                <div class="mb-4">
                  <div class="font-bold text-accent mb-1">Maschinen Details</div>
                  <div class="grid grid-cols-2 gap-x-4 gap-y-1">
                    <span class="text-base-content/70">Name:</span><span>${ticket.machineName}</span>
                    <span class="text-base-content/70">Dossier Nr.:</span><span>${ticket.dossierNumber ?? '-'}</span>
                  </div>
                </div>
              `
            : html`<div class="mb-4 text-warning">Keine Maschine verknüpft</div>`}
          ${ticket.accountName || ticket.accountNumber
            ? html`
                <div class="mb-4">
                  <div class="font-bold text-accent mb-1">Account Details</div>
                  <div class="grid grid-cols-2 gap-x-4 gap-y-1">
                    <span class="text-base-content/70">Name:</span><span>${ticket.accountName ?? '-'}</span>
                    <span class="text-base-content/70">Nummer:</span><span>${ticket.accountNumber ?? '-'}</span>
                    <span class="text-base-content/70">E-Mail:</span><span>${ticket.callerEmail ?? '-'}</span>
                  </div>
                </div>
              `
            : html`<div class="mb-4 text-warning">Kein Account verknüpft</div>`}
          ${ticket.notes && ticket.notes.length > 0
            ? html`
                <div class="mb-4">
                  <div class="font-bold text-accent mb-1">Notizen</div>
                  <ul class="list-disc list-inside">
                    ${ticket.notes.map(n => html`<li class="text-sm">${n}</li>`)}
                  </ul>
                </div>
              `
            : nothing}

          <div class="mt-8">
            <div class="mb-2 sticky top-0 bg-primary-content">
              <button
                class="w-full flex items-center justify-between py-2 px-0 text-left text-base-content/60 hover:bg-base-200/40 focus:outline-none transition-colors rounded"
                @click=${() => setLogOpen(!logOpen)}
                aria-expanded="${logOpen}"
              >
                <span class="text-sm font-bold">Call Log</span>
                <svg
                  class="w-4 h-4 ml-2 transition-transform ${logOpen ? 'rotate-90' : ''}"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            ${logOpen
              ? html`
                  <div class="bg-base-100 border border-base-200 rounded-box p-2 shadow-none">
                    <div class="px-2 py-2 text-sm text-base-content/60">Noch keine Call-Log-Einträge vorhanden.</div>
                  </div>
                `
              : nothing}
          </div>
        </div>
      </div>
    </section>
  `;
});
